import { HttpErrorResponse } from '@angular/common/http';

/** Query params accepted by {@link ApiService}. Nullish values are dropped. */
export type ApiParams = Record<
  string,
  string | number | boolean | ReadonlyArray<string | number | boolean> | null | undefined
>;

export interface ApiRequestOptions {
  /** Query string parameters. */
  params?: ApiParams;
  /** Extra headers merged over the defaults. */
  headers?: Record<string, string>;
  /**
   * Cache the response in TransferState during SSR and reuse it for the first
   * render on the browser, skipping a duplicate request. GET only.
   */
  cache?: boolean;
}

/**
 * Normalised error thrown by {@link ApiService} for every failed request, so
 * callers never have to branch on `HttpErrorResponse` vs. network error.
 */
export class ApiError extends Error {
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
  /** Parsed response body, when the server sent one. */
  readonly body: unknown;
  /** The request URL that failed. */
  readonly url: string | null;

  constructor(response: HttpErrorResponse) {
    super(ApiError.messageFor(response));
    this.name = 'ApiError';
    this.status = response.status;
    this.body = response.error;
    this.url = response.url;
  }

  /** True for 0/5xx — safe to retry. */
  get isServerOrNetwork(): boolean {
    return this.status === 0 || this.status >= 500;
  }

  private static messageFor(response: HttpErrorResponse): string {
    const body = response.error as { message?: unknown; error?: unknown } | null;
    if (body && typeof body.message === 'string') return body.message;
    if (body && typeof body.error === 'string') return body.error;
    if (response.status === 0) return 'Network error — could not reach the server.';
    return response.message || `Request failed with status ${response.status}.`;
  }
}
